import { Autocomplete, Box, FormControl, Grid, ListItem } from "@mui/material";
import CustomInput from "../../components/inputs/CustomInput";
import { GENDER } from "../../utils/constants";
import { findObjectKeyByValue } from "../../utils/helper";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import FileInput from "../../components/inputs/FileInput";
import { CAMP_STATUS } from "../../utils/beneficiary.constant";
import CreatableDropDownComponent from "../../components/custom/CreateableDropodown";
import CampController from "../camp/CreateController";
import { useSelector } from "react-redux";
import MODULES from "../../utils/module.constant";
import { getCampApi } from "../../apis/camp.api";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import {
  getCityApi,
  getCountryApi,
  getStateApi,
} from "../../apis/CountryStateCity.api";

const PersonalDetailsFields = ({
  show,
  fields,
  fieldErrors = {},
  setFields,
  setFieldErrors,
  loading,
}) => {
  const { user } = useSelector((state) => state);

  const clearFieldError = (fieldName) => {
    setFieldErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const updateField = (fieldName, value, extraFields = {}) => {
    setFields((prev) => ({
      ...prev,
      ...extraFields,
      [fieldName]: value,
      err: "",
    }));
    clearFieldError(fieldName);
  };

  return (
    <Box sx={{ width: "100%", display: show ? "block" : "none" }}>
      <Grid container columnSpacing={2}>
        <Grid item sm={12} xs={12}>
          <CreatableDropDownComponent
            onlyDropDown={!user.data?.modules?.includes?.(MODULES.CAMPS)}
            createComponent={<CampController />}
            modalKey="create-camp"
            disabled={loading}
            value={typeof fields.camp === "object" ? fields.camp : null}
            label={"Select Camp*"}
            dropdownProps={{
              error: Boolean(fieldErrors.camp),
              helperText: fieldErrors.camp,
            }}
            getApi={async (params) =>
              await getCampApi({
                ...params,
                status: CAMP_STATUS.OPEN,
                allStatus: true,
                allDetails: true,
              })
            }
            onChange={async (changedVal) => {
              setFields((prev) => ({
                ...prev,
                camp: changedVal ? changedVal : null,
                country: changedVal?.country || "",
                state: changedVal?.state || "",
                district: changedVal?.district || "",
                err: "",
              }));
              clearFieldError("camp");
              clearFieldError("country");
              clearFieldError("state");
              clearFieldError("district");
            }}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <CustomInput
            autoFocus={true}
            disabled={loading}
            value={fields.first_name}
            onChange={(e) => updateField("first_name", e.target.value)}
            type="text"
            label={"First Name*"}
            error={Boolean(fieldErrors.first_name)}
            helperText={fieldErrors.first_name}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <CustomInput
            disabled={loading}
            value={fields.last_name}
            onChange={(e) => updateField("last_name", e.target.value)}
            type="text"
            label={"Last Name"}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <CustomInput
            disabled={loading}
            value={fields.phone}
            onChange={(e) =>
              updateField("phone", e.target.value, {
                family_contact: e.target.value,
              })
            }
            type="text"
            label={"Phone No*"}
            error={Boolean(fieldErrors.phone)}
            helperText={fieldErrors.phone}
          />
        </Grid>

        <Grid item sm={6} xs={8}>
          <CustomInput
            disabled={loading}
            value={fields.email}
            onChange={(e) => updateField("email", e.target.value)}
            type="email"
            label={"Email"}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <Autocomplete
            value={findObjectKeyByValue(fields.gender, GENDER)}
            disableClearable
            onChange={(event, newVal) => {
              updateField("gender", newVal?._id);
            }}
            options={Object.keys(GENDER).map((key) => ({
              label: key,
              _id: GENDER[key],
            }))}
            renderInput={(params) => (
              <CustomInput
                placeholder="Select Gender*"
                {...params}
                error={Boolean(fieldErrors.gender)}
                helperText={fieldErrors.gender}
              />
            )}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <DatePicker
            views={["month", "year", "day"]}
            value={moment(fields.dob)}
            inputFormat="DD/MM/YYYY"
            disableFuture
            onChange={(val) => {
              updateField("dob", val?.toISOString());
            }}
            label="Select DOB*"
            renderInput={(params) => (
              <CustomInput
                placeholder="Select DOB*"
                {...params}
                error={Boolean(fieldErrors.dob)}
                helperText={fieldErrors.dob}
              />
            )}
          />
        </Grid>

        <Grid item sm={4} xs={12}>
          <FormControl fullWidth={true} margin="dense">
            <CustomInput
              disabled={loading}
              value={fields.organization}
              onChange={(e) => updateField("organization", e.target.value)}
              type="text"
              label={"Organization"}
            />
          </FormControl>
        </Grid>

        <Grid item sm={4} xs={12}>
          <FormControl fullWidth={true} margin="dense">
            <CustomInput
              disabled={loading}
              value={fields.department}
              onChange={(e) => updateField("department", e.target.value)}
              type="text"
              label={"Department"}
            />
          </FormControl>
        </Grid>

        <Grid item sm={4} xs={12}>
          <FormControl fullWidth={true} margin="dense">
            <CustomInput
              disabled={loading}
              value={fields.designation}
              onChange={(e) => updateField("designation", e.target.value)}
              type="text"
              label={"Designation"}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <CustomInput
            multiline
            rows={2}
            disabled={loading}
            value={fields.address_line}
            onChange={(e) => updateField("address_line", e.target.value)}
            type="text"
            label={"Address (Street/ Village Name)*"}
            error={Boolean(fieldErrors.address_line)}
            helperText={fieldErrors.address_line}
          />
        </Grid>

        <Grid item sm={3} xs={12}>
          <FormControl fullWidth={true} margin="dense">
            <CustomInput
              disabled={loading}
              value={fields.PIN}
              onChange={(e) => updateField("PIN", e.target.value)}
              type="text"
              label={"PIN"}
              error={Boolean(fieldErrors.PIN)}
              helperText={fieldErrors.PIN}
            />
          </FormControl>
        </Grid>

        <Grid mt={3} item sm={3} xs={12}>
          <AsyncDropDown
            Value={fields?.country ? { _id: 1, name: fields?.country } : null}
            lazyFun={async (para) =>
              await getCountryApi({ ...para, allStatus: true, isName: true })
            }
            OptionComponent={({ option, ...rest }) => {
              return <ListItem {...rest}>{option.name}</ListItem>;
            }}
            onChange={async (changedVal) => {
              setFields((prev) => ({
                ...prev,
                country: changedVal?.name || "",
                taluka: "",
                state: "",
                district: "",
                err: "",
              }));
              clearFieldError("country");
              clearFieldError("state");
              clearFieldError("district");
            }}
            titleKey={"name"}
            valueKey={"_id"}
            InputComponent={(params) => (
              <CustomInput
                placeholder={"Select Country*"}
                label={"Country*"}
                {...params}
                margin="none"
                error={Boolean(fieldErrors.country)}
                helperText={fieldErrors.country}
              />
            )}
          />
        </Grid>

        <Grid mt={3} item sm={3} xs={12}>
          <AsyncDropDown
            key={fields.state}
            Value={fields?.state ? { _id: 1, name: fields?.state } : null}
            lazyFun={async (para) =>
              await getStateApi({
                ...para,
                allStatus: true,
                country: fields?.country,
                isName: true,
              })
            }
            OptionComponent={({ option, ...rest }) => {
              return <ListItem {...rest}>{option.name}</ListItem>;
            }}
            onChange={async (changedVal) => {
              setFields((prev) => ({
                ...prev,
                state: changedVal?.name || "",
                district: "",
                err: "",
              }));
              clearFieldError("state");
              clearFieldError("district");
            }}
            titleKey={"name"}
            valueKey={"_id"}
            InputComponent={(params) => (
              <CustomInput
                placeholder={"Select State*"}
                label={"State*"}
                {...params}
                margin="none"
                error={Boolean(fieldErrors.state)}
                helperText={fieldErrors.state}
              />
            )}
          />
        </Grid>

        <Grid mt={3} item sm={3} xs={12}>
          <AsyncDropDown
            key={fields.state}
            disabled={!fields.state}
            Value={fields?.district ? { _id: 3, name: fields?.district } : null}
            lazyFun={async (para) =>
              await getCityApi({
                ...para,
                allStatus: true,
                state: fields.state,
                isName: true,
              })
            }
            OptionComponent={({ option, ...rest }) => {
              return <ListItem {...rest}>{option.name}</ListItem>;
            }}
            onChange={async (changedVal) => {
              setFields((prev) => ({
                ...prev,
                district: changedVal?.name || "",
                err: "",
              }));
              clearFieldError("district");
            }}
            titleKey={"name"}
            valueKey={"_id"}
            InputComponent={(params) => (
              <CustomInput
                placeholder={"Select District*"}
                label={"District*"}
                {...params}
                margin="none"
                error={Boolean(fieldErrors.district)}
                helperText={fieldErrors.district}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FileInput
            onDelete={() => {
              updateField("photo", "");
            }}
            defaults={fields.photo ? [fields.photo] : undefined}
            title="Photo Identity"
            subTitle="Only png, jpg, jpeg are required."
            onlyImage
            onChange={(newUrl) => {
              updateField("photo", newUrl);
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalDetailsFields;
